import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import ProductMarquee from '../components/ProductMarquee';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselImages = [
    'https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=1000&q=70&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1593305841991-05c297ba4575?w=1000&q=70&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=1000&q=70&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1527814050087-3793815479db?w=1000&q=70&auto=format&fit=crop',
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % carouselImages.length);
    }, 4500);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [featRes, allRes, catRes] = await Promise.all([
        axios.get(`${API}/products?featured=true&light=true&limit=12`),
        axios.get(`${API}/products?light=true&limit=15`),
        axios.get(`${API}/categories`),
      ]);
      setFeaturedProducts(featRes.data);
      setAllProducts(allRes.data);
      const parents = catRes.data.filter((c) => !c.parent_id).slice(0, 6);
      setCategories(parents);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => setCurrentSlide((p) => (p + 1) % carouselImages.length);
  const prevSlide = () =>
    setCurrentSlide((p) => (p - 1 + carouselImages.length) % carouselImages.length);

  const marqueeProducts = (allProducts.length ? allProducts : featuredProducts).slice(0, 8);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Carousel */}
      <section className="relative h-[280px] sm:h-[380px] md:h-[520px] overflow-hidden bg-gray-900">
        <div className="relative h-full">
          {carouselImages.map((img, i) => (
            <div
              key={img}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                i === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img src={img} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center z-10">
            <div className="container mx-auto px-4">
              <div className="max-w-xl text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-4 text-xs uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                  GAMERS CHOICE — Dar El Beïda, Alger
                </div>
                <h1 className="text-4xl md:text-6xl font-extrabold mb-4 leading-tight">
                  <span className="bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent neon-text-cyan">
                    GC STORE
                  </span>
                  <br />
                  <span className="text-white">Gaming • PC • Accessoires</span>
                </h1>
                <p className="text-base md:text-lg text-gray-200 mb-6">
                  Le meilleur du matériel gaming, livré partout en Algérie.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold px-6 py-3 rounded-lg hover:neon-cyan transition"
                    data-testid="hero-cta-products"
                  >
                    Voir tous les produits <ChevronRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white font-bold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                  >
                    Nous contacter
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/40 hover:bg-black/60 backdrop-blur text-white p-3 rounded-full transition"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/40 hover:bg-black/60 backdrop-blur text-white p-3 rounded-full transition"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {carouselImages.map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => setCurrentSlide(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentSlide ? 'bg-cyan-400 w-8' : 'bg-white/40 w-3'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Scrolling products marquee */}
      {marqueeProducts.length > 0 && <ProductMarquee products={marqueeProducts} />}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-14 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900" data-testid="featured-products-title">
                ⭐ <span className="bg-gradient-to-r from-cyan-500 to-purple-600 bg-clip-text text-transparent">Produits Vedettes</span>
              </h2>
              <Link
                to="/products"
                className="text-cyan-600 hover:text-purple-600 font-semibold flex items-center gap-1 transition"
              >
                Voir tout <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {featuredProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl p-10 md:p-14 text-center text-white shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-3">
              Visitez notre magasin à Dar El Beïda
            </h2>
            <p className="text-white/90 mb-6">
              N°13 Lotissement Mohamed Essadik Benyahia, Dar El Beïda, Alger
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-purple-600 font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition"
              data-testid="contact-button"
            >
              Nous Contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
